import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';

import { UseOutletContextQuote } from './Quote';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { QuoteDA } from '../../../../services/admin/quote.service';
import { OrderDA } from '../../../../services/admin/order.service';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { Quote } from '../../../../models/quote.model';
import { Order } from '../../../../models/order.model';
import { modalShow } from '../../../../utils/modal.util';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { colorStatusQuote } from '../../../../scripts/badge.color.script';
import { normalizeStatus, statusDocumentValue } from '../../../../libraries/status.document.library';
import { getError } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { normalizeContactData } from '../../../../scripts/format.value.script';
import { MediaDA } from '../../../../services/admin/media.service';

import AppSaleViewDetail from '../component/SaleViewDetail';
import AppSaleViewRequirements from '../component/SaleViewRequirements';
import AppSaleViewAmount from '../component/SaleViewAmount';
import AppSaleViewSignatures from '../component/SaleViewSignatures';
import AppPurchaseViewDraft from '../../purchase/component/PurchaseViewDraft';
import AppModalQuoteNotification from '../../../../components/modal/ModalQuoteNotification';
import AppPreloader from '../../../../components/Preloader';
import AppModalSendEmail from '../../../../components/modal/ModalSendEmail';
import DocumentNavigation from '../../../../components/element/DocumentNavigation';

export interface QuoteViewPageProps {};

let errorResponse: Error, quoteResponse: Quote, orderResponse: Order;

const QuoteViewPage: React.FunctionComponent<QuoteViewPageProps> = props => {
  const {setRoute, userLoggedIn, appServiceForUser, companyForUser, settingsForCompany} = UseOutletContextQuote()
  const {lang} = StateLanguage()
  const param = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [quote, setQuote] = useState<Quote | null>(null)
  const setQuoteNormalized: Dispatch<SetStateAction<Quote | null>> = (value) => {
    setQuote((prev) => {
      const next = typeof value === 'function' ? (value as (p: Quote | null) => Quote | null)(prev) : value
      if (!next) return next
      return { ...next, status: normalizeStatus(next.status) }
    })
  }
  const [vouchers, setVouchers] = useState({list: true, value: [] as {name: string, size: number, type: string, view: string, file: any}[], valid: [] as {name: boolean, file: boolean}[]})

  const loadQuote = async (id_company: string, id_quote: string) => {
    await QuoteDA.getQuote(id_company, id_quote).then( async (response) => {
      if (response.status === 200) {
        quoteResponse = response.data
        quoteResponse.status = normalizeStatus(quoteResponse.status)
        normalizeContactData(quoteResponse.customer)

        let tempVouchersValue: {name: string, size: number, type: string, view: string, file: any}[] = []
        let tempVouchersValid: {name: boolean, file: boolean}[] = []

        for (let voucher of (quoteResponse.vouchers ?? [])) {
          const vName = voucher.original_name ?? voucher.name ?? ''
          const vType = voucher.content_type ?? 'application/pdf'
          let vView = ''

          if (voucher.id_document_inbox && companyForUser) {
            const signedUrl = await MediaDA.getDocumentSignedUrl(companyForUser.id, voucher.id_document_inbox)
            if (signedUrl) vView = signedUrl
          } else if (voucher.file) {
            vView = voucher.file
          }

          tempVouchersValue.push({
            name: vName,
            size: voucher.size ?? 0,
            type: vType,
            view: vView,
            file: vView
          })
          tempVouchersValid.push({ name: !!vName, file: !!vView })
        }

        setVouchers({list: true, value: tempVouchersValue, valid: tempVouchersValid})
        setQuote(quoteResponse)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    }) 
  }

  const submitQuoteStatus = async (item: string) => {
    if (appServiceForUser && companyForUser && quote && item === 'approved') {
      GlobalSwal.fire({
        title: `${lang.labels.youWantToChangeQuoteStatusTo} ${statusDocumentValue(lang, item).toLowerCase()}?`,
        text: lang.labels.youWillNotBeAbleToUndo,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${lang.labels.yes}, ${statusDocumentValue(lang, item).toLowerCase()}`,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          await QuoteDA.putQuoteStatus(companyForUser.id, quote.id, item).then( async (response) => {
            if (response.status === 200) {
              quoteResponse = response.data
              setQuote(quoteResponse)

              GlobalSwal.fire({
                title: lang.labels.successfullyUpdatedStatus,
                text: lang.labels.actionCompletedReturningToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch( (error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    } else {
      // Status is decided by the modal (ModalQuoteNotification hardcodes 'rejected')
      modalShow('modal-quote-notification')
    }
  }

  const executeShowModalSendEmail = () => {
    modalShow('modal-send-email')
  }

  const submitQuoteSend = async () => {
    if (appServiceForUser && companyForUser && quote) {
      GlobalSwal.fire({
        title: lang.labels.sendToCustomer ?? 'Send to customer',
        text: lang.labels.youWillNotBeAbleToUndo,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesSend,
        cancelButtonText: lang.labels.noCancel,
        customClass: {confirmButton:'btn btn-primary', cancelButton:'btn btn-secondary'}
      }).then(async (result) => {
        if (result.isConfirmed) {
          await QuoteDA.sendQuote(companyForUser.id, quote.id, 'in_app').then( async (response) => {
            if (response.status === 200 || response.status === 201) {
              quoteResponse = response.data
              setQuote(quoteResponse)

              GlobalSwal.fire({
                title: lang.labels.successfullyQuoteSent,
                text: lang.labels.actionCompletedReturningToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch( (error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  const submitOrderCreate = async () => {
    if (companyForUser && quote) {
      GlobalSwal.fire({
        title: lang.labels.youWantToGenerateAnOrderFromThisQuote,
        text: lang.labels.thisOptionIsUniqueAndNewOrderCannotBeCreatedFromThisQuote,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesGenerate,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          await OrderDA.postOrderReference(companyForUser.id, quote.id).then( (response) => {
            if (response.status === 201) {
              orderResponse = response.data

              GlobalSwal.fire({
                title: lang.labels.successfullyCreatedOrder,
                text: lang.labels.actionCompletedRedirectingToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              }).then( () => {
                navigate('/main/sale/order/view/' + orderResponse.id)
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch( (error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  useEffect( () => {
    if (companyForUser && param.id) {
      loadQuote(companyForUser.id, param.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.quotes, branch: lang.labels.viewQuote}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { userLoggedIn && companyForUser && settingsForCompany && quote
        ?
        <div className="d-flex flex-column flex-lg-row">
          <div className="flex-lg-row-fluid order-2 order-lg-1 mb-5 mb-lg-0 me-lg-5">
          <div className="card card-flush mb-5">
            <div className="card-header pt-3">
              <div className="card-title align-items-start flex-column">
                <div className="d-flex align-items-center gap-3">
                  <h1 className="mb-0">{quote.quote_number}</h1>
                  {companyForUser && param.id && <DocumentNavigation navKey="nav_quotes" currentId={param.id} companyId={companyForUser.id} basePath="/main/sale/quote/view/" />}
                </div>
                <label className="fs-6 fw-bold mt-1">
                  {lang.labels.status}:
                  <span className={`badge badge-light-${colorStatusQuote(quote.status)} text-uppercase fs-7 ms-2`}>{statusDocumentValue(lang, quote.status)}</span>
                </label>
                { (quote as any).remote_decision && (quote as any).remote_decision !== 'none' &&
                  <label className="fs-6 fw-bold mt-1">
                    {lang.labels.recipientDecision}:
                    <span className={`badge badge-light-${(quote as any).remote_decision === 'accepted' ? 'success' : 'danger'} text-uppercase fs-7 ms-2`}>
                      {(quote as any).remote_decision === 'accepted' ? lang.labels.decisionAccepted : lang.labels.decisionDeclined}
                    </span>
                    { (quote as any).remote_decided_at &&
                      <span className="text-muted fs-7 ms-2">{dateFormat(lang.code, (quote as any).remote_decided_at)}</span>
                    }
                  </label>
                }
              </div>
              <div className="card-toolbar">
                { quote.status === "in-progress" &&
                  <>
                    <Link to={"/main/sale/quote/update/" + quote.id} className="btn btn-primary mb-1 me-2">
                      <div className="d-flex align-items-center">
                        <span className="svg-icon svg-icon-2 ms-0">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path opacity="0.3" d="M2 4.63158C2 3.1782 3.1782 2 4.63158 2H13.47C14.0155 2 14.278 2.66919 13.8778 3.04006L12.4556 4.35821C11.9009 4.87228 11.1726 5.15789 10.4163 5.15789H7.1579C6.05333 5.15789 5.15789 6.05333 5.15789 7.1579V16.8421C5.15789 17.9467 6.05333 18.8421 7.1579 18.8421H16.8421C17.9467 18.8421 18.8421 17.9467 18.8421 16.8421V13.7518C18.8421 12.927 19.1817 12.1387 19.7809 11.572L20.9878 10.4308C21.3703 10.0691 22 10.3403 22 10.8668V19.3684C22 20.8218 20.8218 22 19.3684 22H4.63158C3.1782 22 2 20.8218 2 19.3684V4.63158Z" fill="black"/>
                            <path d="M10.9256 11.1882C10.5351 10.7977 10.5351 10.1645 10.9256 9.77397L18.0669 2.6327C18.8479 1.85165 20.1143 1.85165 20.8953 2.6327L21.3665 3.10391C22.1476 3.88496 22.1476 5.15129 21.3665 5.93234L14.2252 13.0736C13.8347 13.4641 13.2016 13.4641 12.811 13.0736L10.9256 11.1882Z" fill="black"/>
                            <path d="M8.82343 12.0064L8.08852 14.3348C7.8655 15.0414 8.46151 15.7366 9.19388 15.6242L11.8974 15.2092C12.4642 15.1222 12.6916 14.4278 12.2861 14.0223L9.98595 11.7221C9.61452 11.3507 8.98154 11.5055 8.82343 12.0064Z" fill="black"/>
                          </svg>
                        </span>
                        {lang.labels.edit}
                      </div>
                    </Link>
                    { quote.customer?.is_user
                      ?
                      <button className="btn btn-primary mb-1 me-2" type="button" onClick={submitQuoteSend}>
                        <div className="d-flex align-items-center">
                          <span className="svg-icon svg-icon-2 ms-0">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M15.43 8.56949L10.744 15.1395C10.6422 15.282 10.5804 15.4492 10.5651 15.6236C10.5498 15.7981 10.5815 15.9734 10.657 16.1315L13.194 21.4425C13.2737 21.6097 13.3991 21.751 13.5557 21.8499C13.7123 21.9488 13.8938 22.0014 14.079 22.0015H14.117C14.3087 21.9941 14.4941 21.9307 14.6502 21.8191C14.8062 21.7075 14.9261 21.5526 14.995 21.3735L21.933 3.33649C22.0011 3.15918 22.0164 2.96594 21.977 2.78013C21.9376 2.59432 21.8452 2.4239 21.711 2.28949L15.43 8.56949Z" fill="black"/>
                              <path opacity="0.3" d="M20.664 2.06648L2.62602 9.00148C2.44768 9.07085 2.29348 9.19082 2.1824 9.34663C2.07131 9.50244 2.00818 9.68731 2.00074 9.87853C1.99331 10.0697 2.04189 10.259 2.14054 10.4229C2.23919 10.5869 2.38359 10.7185 2.55601 10.8015L7.86601 13.3365C8.02383 13.4126 8.19925 13.4448 8.37382 13.4297C8.54839 13.4145 8.71565 13.3526 8.85801 13.2505L15.43 8.56548L21.711 2.28448C21.5762 2.15096 21.4055 2.05932 21.2198 2.02064C21.034 1.98196 20.8409 1.99788 20.664 2.06648Z" fill="black"/>
                            </svg>
                          </span>
                          {lang.labels.sendToCustomer ?? lang.labels.send}
                        </div>
                      </button>
                      :
                      quote.customer?.data?.email &&
                      <button className="btn btn-primary mb-1 me-2" type="button" onClick={executeShowModalSendEmail}>
                        <div className="d-flex align-items-center">
                          <span className="svg-icon svg-icon-2 ms-0">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M15.43 8.56949L10.744 15.1395C10.6422 15.282 10.5804 15.4492 10.5651 15.6236C10.5498 15.7981 10.5815 15.9734 10.657 16.1315L13.194 21.4425C13.2737 21.6097 13.3991 21.751 13.5557 21.8499C13.7123 21.9488 13.8938 22.0014 14.079 22.0015H14.117C14.3087 21.9941 14.4941 21.9307 14.6502 21.8191C14.8062 21.7075 14.9261 21.5526 14.995 21.3735L21.933 3.33649C22.0011 3.15918 22.0164 2.96594 21.977 2.78013C21.9376 2.59432 21.8452 2.4239 21.711 2.28949L15.43 8.56949Z" fill="black"/>
                              <path opacity="0.3" d="M20.664 2.06648L2.62602 9.00148C2.44768 9.07085 2.29348 9.19082 2.1824 9.34663C2.07131 9.50244 2.00818 9.68731 2.00074 9.87853C1.99331 10.0697 2.04189 10.259 2.14054 10.4229C2.23919 10.5869 2.38359 10.7185 2.55601 10.8015L7.86601 13.3365C8.02383 13.4126 8.19925 13.4448 8.37382 13.4297C8.54839 13.4145 8.71565 13.3526 8.85801 13.2505L15.43 8.56548L21.711 2.28448C21.5762 2.15096 21.4055 2.05932 21.2198 2.02064C21.034 1.98196 20.8409 1.99788 20.664 2.06648Z" fill="black"/>
                            </svg>
                          </span>
                          {lang.labels.sendByEmail ?? lang.labels.send}
                        </div>
                      </button>
                    }
                    <Dropdown>
                      <Dropdown.Toggle variant="btn btn-primary mb-1 me-2">
                        <div className="d-flex align-items-center">
                          {lang.labels.actions}
                          <span className="svg-icon svg-icon-2 me-0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M11.4343 12.7344L7.25 8.55005C6.83579 8.13583 6.16421 8.13584 5.75 8.55005C5.33579 8.96426 5.33579 9.63583 5.75 10.05L11.2929 15.5929C11.6834 15.9835 12.3166 15.9835 12.7071 15.5929L18.25 10.05C18.6642 9.63584 18.6642 8.96426 18.25 8.55005C17.8358 8.13584 17.1642 8.13584 16.75 8.55005L12.5657 12.7344C12.2533 13.0468 11.7467 13.0468 11.4343 12.7344Z" fill="black"></path>
                            </svg>
                          </span>
                        </div>
                      </Dropdown.Toggle>
                      <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg-light-primary w-200px p-3" align="end">
                        <Dropdown.Item bsPrefix="menu-item" onClick={() => submitQuoteStatus("approved")}>
                          <p className="menu-link fw-bold px-5 m-0">{lang.labels.approveQuote}</p>
                        </Dropdown.Item>
                        <Dropdown.Item bsPrefix="menu-item" onClick={() => submitQuoteStatus("rejected")}>
                          <p className="menu-link fw-bold px-5 m-0">{lang.labels.rejectQuote}</p>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </>
                }
                { quote.status === "pending" &&
                  <>
                    <button className="btn btn-primary mb-1 me-2" type="button" onClick={() => submitQuoteStatus("approved")}>
                      <div className="d-flex align-items-center">
                        <span className="svg-icon svg-icon-2 ms-0">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path opacity="0.3" d="M10.3 14.3L11 13.6L7.70002 10.3C7.30002 9.9 6.7 9.9 6.3 10.3C5.9 10.7 5.9 11.3 6.3 11.7L10.3 15.7C9.9 15.3 9.9 14.7 10.3 14.3Z" fill="black"/>
                            <path d="M22 12C22 17.5 17.5 22 12 22C6.5 22 2 17.5 2 12C2 6.5 6.5 2 12 2C17.5 2 22 6.5 22 12ZM11.7 15.7L17.7 9.70001C18.1 9.30001 18.1 8.69999 17.7 8.29999C17.3 7.89999 16.7 7.89999 16.3 8.29999L11 13.6L7.70001 10.3C7.30001 9.89999 6.69999 9.89999 6.29999 10.3C5.89999 10.7 5.89999 11.3 6.29999 11.7L10.3 15.7C10.5 15.9 10.8 16 11 16C11.2 16 11.5 15.9 11.7 15.7Z" fill="black"/>
                          </svg>
                        </span>
                        {lang.labels.approve}
                      </div>
                    </button>
                    <button className="btn btn-primary mb-1 me-2" type="button" onClick={() => submitQuoteStatus("rejected")}>
                      <div className="d-flex align-items-center">
                        <span className="svg-icon svg-icon-2 ms-0">
                          <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path opacity="0.3" d="M8.9 21L7.19999 22.6999C6.79999 23.0999 6.2 23.0999 5.8 22.6999L4.1 21H8.9ZM4 16.0999L2.3 17.8C1.9 18.2 1.9 18.7999 2.3 19.1999L4 20.9V16.0999ZM19.3 9.1999L15.8 5.6999C15.4 5.2999 14.8 5.2999 14.4 5.6999L9 11.0999V21L19.3 10.6999C19.7 10.2999 19.7 9.5999 19.3 9.1999Z" fill="black"/>
                            <path d="M21 15V20C21 20.6 20.6 21 20 21H11.8L18.8 14H20C20.6 14 21 14.4 21 15ZM10 21V4C10 3.4 9.6 3 9 3H4C3.4 3 3 3.4 3 4V21C3 21.6 3.4 22 4 22H9C9.6 22 10 21.6 10 21ZM7.5 18.5C7.5 19.1 7.1 19.5 6.5 19.5C5.9 19.5 5.5 19.1 5.5 18.5C5.5 17.9 5.9 17.5 6.5 17.5C7.1 17.5 7.5 17.9 7.5 18.5Z" fill="black"/>
                          </svg>
                        </span>
                        {lang.labels.reject}
                      </div>
                    </button>
                  </>
                }
                { quote.status === "approved" &&
                  <button className="btn btn-primary mb-1 me-2" type="button" onClick={submitOrderCreate}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-2 ms-0">
                        <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path opacity="0.3" d="M8.9 21L7.19999 22.6999C6.79999 23.0999 6.2 23.0999 5.8 22.6999L4.1 21H8.9ZM4 16.0999L2.3 17.8C1.9 18.2 1.9 18.7999 2.3 19.1999L4 20.9V16.0999ZM19.3 9.1999L15.8 5.6999C15.4 5.2999 14.8 5.2999 14.4 5.6999L9 11.0999V21L19.3 10.6999C19.7 10.2999 19.7 9.5999 19.3 9.1999Z" fill="black"/>
                          <path d="M21 15V20C21 20.6 20.6 21 20 21H11.8L18.8 14H20C20.6 14 21 14.4 21 15ZM10 21V4C10 3.4 9.6 3 9 3H4C3.4 3 3 3.4 3 4V21C3 21.6 3.4 22 4 22H9C9.6 22 10 21.6 10 21ZM7.5 18.5C7.5 19.1 7.1 19.5 6.5 19.5C5.9 19.5 5.5 19.1 5.5 18.5C5.5 17.9 5.9 17.5 6.5 17.5C7.1 17.5 7.5 17.9 7.5 18.5Z" fill="black"/>
                        </svg>
                      </span>
                      {lang.labels.generateOrder}
                    </div>
                  </button>
                }
                <Link to={"/main/sale/quote/create/reference/" + quote.id} className="btn btn-primary mb-1">
                  <div className="d-flex align-items-center">
                    <span className="svg-icon svg-icon-2 ms-0">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect opacity="0.5" x="7" y="2" width="14" height="16" rx="3" fill="black"/>
                        <rect x="3" y="6" width="14" height="16" rx="3" fill="black"/>
                      </svg>
                    </span>
                    {lang.labels.makeCopy}
                  </div>
                </Link>
              </div>
            </div>
            <AppSaleViewDetail sale={"quote"} record={quote}></AppSaleViewDetail>
          </div>
          { (quote as any).remote_decision === 'declined' && (quote as any).remote_decline_reason &&
            <div className="card card-flush mb-5">
              <div className="card-body py-4">
                <div className="d-flex align-items-start">
                  <span className="badge badge-light-danger text-uppercase fs-8 me-3">{lang.labels.declineReason}</span>
                  <span className="fw-bold text-gray-800">{(quote as any).remote_decline_reason}</span>
                </div>
              </div>
            </div>
          }
          <AppSaleViewRequirements record={quote}></AppSaleViewRequirements>
          <AppSaleViewAmount sale={"quote"} record={quote}></AppSaleViewAmount>
          <AppSaleViewSignatures record={quote}></AppSaleViewSignatures>
          </div>
          <AppPurchaseViewDraft record={quote} vouchers={vouchers} showGeneratedPdf={true} companyId={companyForUser?.id} pdfPath="quotes" documentId={quote?.id}></AppPurchaseViewDraft>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
      <AppModalQuoteNotification appServiceForUser={appServiceForUser} companyForUser={companyForUser} quote={quote} setQuote={setQuoteNormalized}></AppModalQuoteNotification>
      { companyForUser && quote &&
        <AppModalSendEmail companyId={companyForUser.id} documentType="quotes" documentId={quote.id} documentNumber={quote.quote_number} recipientEmail={quote.customer?.data?.email || ''} companyName={companyForUser.name} companyEmail={companyForUser.email || ''}></AppModalSendEmail>
      }
    </div>
  )
}

export default QuoteViewPage;
